import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import SpiceSpectrum from '../components/SpiceSpectrum';
import ContactModal from '../components/ContactModal';
import FeaturedIn from '../components/FeaturedIn';
import HeroExpertTrust from '../components/HeroExpertTrust';
import HowItWorks from '../components/HowItWorks';
import MatchingDemo from '../components/MatchingDemo';
import NavDrawer from '../components/NavDrawer';
import OnboardingFlow from '../components/OnboardingFlow';
import ProfessionalEndorsements from '../components/ProfessionalEndorsements';
import SessionResumeBanner from '../components/SessionResumeBanner';
import useExistingSession from '../hooks/useExistingSession';
import { FOOTER_NAV_ITEMS, getFooterNavHref } from '../lib/siteNavigation';
import { useCurrency } from '../contexts/CurrencyContext';

export default function LandingPage() {
  const { config: currencyConfig } = useCurrency();
  const { session: existingSession, isChecking, dismiss } = useExistingSession();
  const [contactOpen, setContactOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const scrollToOnboarding = (behavior: ScrollBehavior = 'smooth') => {
    const el = document.getElementById('onboarding');
    if (!el) {
      window.scrollTo({ top: 0, behavior });
      return;
    }

    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior });
  };

  useEffect(() => {
    if (searchParams.get('contact') !== '1') return;

    setContactOpen(true);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('contact');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (location.hash !== '#onboarding') return;

    const rafId = window.requestAnimationFrame(() => {
      scrollToOnboarding('smooth');
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [location.hash]);

  return (
    <div className="min-h-screen flex flex-col text-white">
      {/* Sticky header */}
      <header className="fixed top-0 w-full z-50 px-6 py-4 flex justify-between items-center bg-deep-purple/80 backdrop-blur-md border-b border-white/10">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl">🔥</span>
          <span className="font-extrabold text-xl tracking-tight">OurSexQuiz</span>
        </Link>
        <button
          onClick={() => setMenuOpen(true)}
          className="nav-menu-trigger"
          aria-label="Open menu"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </svg>
        </button>
      </header>

      <main className="pt-24 pb-20 px-6 max-w-md md:max-w-2xl mx-auto w-full">
        {/* Resume banner — in flow, sticky below header */}
        {existingSession && !isChecking && (
          <SessionResumeBanner session={existingSession} onDismiss={dismiss} />
        )}

        {/* Hero */}
        <section className="text-center mb-20">
          <HeroExpertTrust />

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">
            Ready to discover<br /><span className="text-gradient">shared</span> desires?
          </h1>

          <p className="text-slate-400 text-lg md:text-xl mb-8">
            Only see what you both say yes to, and<br />explore something new tonight!
          </p>

          {/* Onboarding card */}
          <div id="onboarding" className="scroll-mt-24 onboarding-surface p-6 sm:p-8 rounded-[2.5rem] space-y-5 text-white relative overflow-hidden">
            <div className="relative z-10">
              <OnboardingFlow mode="landing" />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <HowItWorks />

        {/* Matching demo */}
        <MatchingDemo />

        {/* Categories preview */}
        <SpiceSpectrum />

        {/* Professional endorsements */}
        <ProfessionalEndorsements />

        {/* Featured in */}
        <FeaturedIn />

        {/* Final CTA */}
        <section className="text-center py-12">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 leading-tight">
            Spice things up tonight 🌶️
          </h2>

          <p className="text-slate-400 mb-10 text-sm">
            Whether you&apos;re a new couple looking to break the ice, or long-term partners looking to break a routine.
          </p>

          <button
            onClick={() => scrollToOnboarding('smooth')}
            className="w-full gradient-cta text-white font-extrabold py-5 rounded-2xl text-xl mb-4"
          >
            Let&apos;s find out 🔥
          </button>

          <p className="text-xs text-slate-500 font-medium">
            Free to take &middot; Full results for {currencyConfig.label}
          </p>
        </section>

        {/* Footer */}
        <footer className="mt-20 pt-10 border-t border-white/10 text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="text-xl">🔥</span>
            <span className="font-extrabold text-lg">OurSexQuiz</span>
          </div>
          <div className="flex justify-center gap-6 text-sm text-slate-400 font-medium mb-10">
            {FOOTER_NAV_ITEMS.map((item) => {
              if (item.key === 'contact') {
                return (
                  <button
                    key={item.key}
                    onClick={() => setContactOpen(true)}
                    className="hover:text-primary transition-colors"
                  >
                    {item.label}
                  </button>
                );
              }

              const to = getFooterNavHref(item.key, 'landing');

              if (!to) return null;

              return (
                <Link key={item.key} to={to} className="hover:text-primary transition-colors">
                  {item.label}
                </Link>
              );
            })}
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/20">
            Made with ❤️ in Amsterdam
          </p>
        </footer>
      </main>

      <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
      <NavDrawer
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onContactClick={() => setContactOpen(true)}
        onPrimaryAction={() => scrollToOnboarding('smooth')}
        primaryActionLabel="Start Quiz"
      />
    </div>
  );
}
